import React, { useState, useEffect } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { Zap, Users, Clock, Star, FileDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_TH,
  GLASS_ROW,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassChip,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassTooltip,
  GlassEmpty,
  GlassDonut,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const TIME_OPTIONS = ['This Month', 'This Quarter', 'This Year', 'All Time'].map(period => ({ value: period, label: period }));

// Donut + legend chips with each slice's share, replacing the old labelled pie
const ShareDonut = ({ data, darkMode, centerValue, centerLabel, unit }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (data.length === 0) return <GlassEmpty className="h-64">No data available</GlassEmpty>;
  return (
    <>
      <div className="mt-4 h-60">
        <GlassDonut data={data} dataKey="value" nameKey="name" inner={82} outer={94} darkMode={darkMode} unit={unit}>
          <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{centerValue ?? total.toLocaleString()}</div>
          <div className="text-xs text-gray-500 dark:text-white/[0.45]">{centerLabel}</div>
        </GlassDonut>
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {data.map(entry => (
          <GlassChip
            key={entry.name}
            color={entry.color}
            label={entry.name}
            value={`${total ? ((entry.value / total) * 100).toFixed(0) : 0}%`}
          />
        ))}
      </div>
    </>
  );
};

const BreakthroughsReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeFilter, setTimeFilter] = useState('This Year');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock data for breakthrough sessions (community calls led by experts)
  const [breakthroughData, setBreakthroughData] = useState({
    overview: {
      totalSessions: 0,
      totalParticipants: 0,
      avgParticipantsPerSession: 0,
      totalDuration: 0,
      avgSessionDuration: 0,
      expertLedSessions: 0,
      participantSatisfaction: 0
    },
    sessionsByExpert: [],
    sessionsData: [],
    participationData: [],
    satisfactionData: [],
    expertPerformance: []
  });

  // Mock data initialization
  useEffect(() => {
    // Initialize with sample data for breakthrough sessions
    const sampleData = {
      overview: {
        totalSessions: 48,
        totalParticipants: 1247,
        avgParticipantsPerSession: 26.0,
        totalDuration: 2880, // minutes
        avgSessionDuration: 60.0,
        expertLedSessions: 48,
        participantSatisfaction: 4.7
      },
      sessionsByExpert: [
        { name: 'Sarah Johnson', value: 12, color: GLASS_PALETTE[0] },
        { name: 'Mike Chen', value: 10, color: GLASS_PALETTE[1] },
        { name: 'Emily Rodriguez', value: 8, color: GLASS_PALETTE[2] },
        { name: 'David Kim', value: 7, color: GLASS_PALETTE[3] },
        { name: 'Lisa Wang', value: 6, color: GLASS_PALETTE[4] },
        { name: 'Others', value: 5, color: GLASS_PALETTE[5] }
      ],
      sessionsData: [
        { period: 'Jan', sessions: 4, participants: 98, satisfaction: 4.6 },
        { period: 'Feb', sessions: 5, participants: 127, satisfaction: 4.7 },
        { period: 'Mar', sessions: 3, participants: 78, satisfaction: 4.5 },
        { period: 'Apr', sessions: 6, participants: 156, satisfaction: 4.8 },
        { period: 'May', sessions: 4, participants: 105, satisfaction: 4.6 },
        { period: 'Jun', sessions: 5, participants: 132, satisfaction: 4.7 }
      ],
      participationData: [
        { name: 'Regular Attendees', value: 45, color: GLASS_TONE.mint },
        { name: 'Occasional Attendees', value: 35, color: GLASS_TONE.amber },
        { name: 'New Participants', value: 20, color: GLASS_TONE.blue }
      ],
      satisfactionData: [
        { period: 'Jan', rating: 4.6, feedback: 23 },
        { period: 'Feb', rating: 4.7, feedback: 28 },
        { period: 'Mar', rating: 4.5, feedback: 19 },
        { period: 'Apr', rating: 4.8, feedback: 31 },
        { period: 'May', rating: 4.6, feedback: 25 },
        { period: 'Jun', rating: 4.7, feedback: 29 }
      ],
      expertPerformance: [
        {
          id: 1,
          name: 'Sarah Johnson',
          sessions: 12,
          totalParticipants: 312,
          avgParticipants: 26.0,
          avgSatisfaction: 4.8,
          totalDuration: 720,
          topics: ['Business Growth', 'Marketing Strategy', 'Leadership'],
          status: 'Active'
        },
        {
          id: 2,
          name: 'Mike Chen',
          sessions: 10,
          totalParticipants: 258,
          avgParticipants: 25.8,
          avgSatisfaction: 4.6,
          totalDuration: 600,
          topics: ['Technology', 'Operations', 'Process Improvement'],
          status: 'Active'
        },
        {
          id: 3,
          name: 'Emily Rodriguez',
          sessions: 8,
          totalParticipants: 204,
          avgParticipants: 25.5,
          avgSatisfaction: 4.7,
          totalDuration: 480,
          topics: ['Sales', 'Customer Relations', 'Team Building'],
          status: 'Active'
        },
        {
          id: 4,
          name: 'David Kim',
          sessions: 7,
          totalParticipants: 189,
          avgParticipants: 27.0,
          avgSatisfaction: 4.9,
          totalDuration: 420,
          topics: ['Finance', 'Analytics', 'Strategic Planning'],
          status: 'Active'
        },
        {
          id: 5,
          name: 'Lisa Wang',
          sessions: 6,
          totalParticipants: 156,
          avgParticipants: 26.0,
          avgSatisfaction: 4.5,
          totalDuration: 360,
          topics: ['HR', 'Training', 'Development'],
          status: 'Active'
        }
      ]
    };

    setBreakthroughData(sampleData);
    setLoading(false);
  }, []);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const exportToPDF = () => {
    const pdf = new jsPDF();
    pdf.setFontSize(20);
    pdf.text('Breakthrough Sessions Report', 20, 20);

    pdf.setFontSize(12);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    pdf.text(`Time Period: ${timeFilter}`, 20, 40);

    pdf.text(`Total Sessions: ${breakthroughData.overview.totalSessions}`, 20, 60);
    pdf.text(`Total Participants: ${breakthroughData.overview.totalParticipants}`, 20, 70);
    pdf.text(`Average Participants per Session: ${breakthroughData.overview.avgParticipantsPerSession}`, 20, 80);
    pdf.text(`Average Session Duration: ${formatDuration(breakthroughData.overview.avgSessionDuration)}`, 20, 90);
    pdf.text(`Participant Satisfaction: ${breakthroughData.overview.participantSatisfaction}/5.0`, 20, 100);

    pdf.save('breakthrough-sessions-report.pdf');
  };

  if (loading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex min-h-[60vh] items-center justify-center">
          <BBALoader size={96} />
        </div>
      </GlassPage>
    );
  }

  if (error) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="text-center">
            <p className={`mb-4 ${GLASS_TEXT.red}`}>{error}</p>
            <GlassAccentButton onClick={() => window.location.reload()}>
              Retry
            </GlassAccentButton>
          </div>
        </div>
      </GlassPage>
    );
  }

  const chart = glassChartTheme(darkMode);

  const stats = [
    { icon: Zap, value: breakthroughData.overview.totalSessions, label: 'Total Sessions' },
    { icon: Users, value: breakthroughData.overview.totalParticipants.toLocaleString(), label: 'Total Participants' },
    { icon: Clock, value: formatDuration(breakthroughData.overview.avgSessionDuration), label: 'Avg Duration' },
    { icon: Star, value: `${breakthroughData.overview.participantSatisfaction}/5.0`, label: 'Satisfaction' },
  ];

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        title={<>Breakthroughs<br />Reports</>}
        stats={stats}
        statsKey={timeFilter}
        controls={
          <SlidingPills size="sm" options={TIME_OPTIONS} value={timeFilter} onChange={setTimeFilter} ariaLabel="Timeframe" />
        }
      />

      <div className="space-y-4">
        {/* Charts Section */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Participation Over Time */}
          <PinnableCard id="participation-over-time" title="Participation Over Time">
          <GlassCard className="p-5" delay={0}>
            <GlassCardHeader title="Participation Over Time" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.lavender} label="Sessions" />
              <GlassChip color={GLASS_TONE.mint} label="Participants" />
            </div>
            <div className="mt-4 h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={breakthroughData.sessionsData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bt-sessions-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GLASS_TONE.lavender} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={GLASS_TONE.lavender} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="bt-participants-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GLASS_TONE.mint} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={GLASS_TONE.mint} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                  <XAxis dataKey="period" tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<GlassTooltip />} cursor={{ stroke: chart.grid }} />
                  <Area type="monotone" dataKey="sessions" stroke={GLASS_TONE.lavender} strokeWidth={2} fill="url(#bt-sessions-fill)" name="Sessions" animationDuration={700} />
                  <Area type="monotone" dataKey="participants" stroke={GLASS_TONE.mint} strokeWidth={2} fill="url(#bt-participants-fill)" name="Participants" animationDuration={700} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
          </PinnableCard>

          {/* Expert Distribution */}
          <PinnableCard id="sessions-by-expert" title="Sessions Led by Expert">
          <GlassCard className="p-5" delay={80}>
            <GlassCardHeader title="Sessions Led by Expert" />
            <ShareDonut data={breakthroughData.sessionsByExpert} darkMode={darkMode} centerLabel="sessions" unit="sessions" />
          </GlassCard>
          </PinnableCard>
        </div>

        {/* Satisfaction Trends */}
        <PinnableCard id="satisfaction-trends" title="Participant Satisfaction Trends" size="wide">
        <GlassCard className="p-5" delay={140}>
          <GlassCardHeader title="Participant Satisfaction Trends" />
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breakthroughData.satisfactionData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barCategoryGap="24%">
                <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                <XAxis dataKey="period" tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis domain={[4.0, 5.0]} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<GlassTooltip />} cursor={{ fill: chart.cursor }} />
                <Bar dataKey="rating" fill={GLASS_TONE.amber} name="Satisfaction Rating" radius={[12, 12, 12, 12]} maxBarSize={56} background={{ fill: chart.track, radius: 12 }} animationDuration={700} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
        </PinnableCard>

        {/* Expert Performance Table */}
        <PinnableCard id="expert-performance" title="Expert Performance Overview" size="wide">
        <GlassCard className="p-5" delay={200}>
          <GlassCardHeader
            title="Expert Performance Overview"
            right={(
              <GlassAccentButton onClick={exportToPDF}>
                <FileDown className="h-4 w-4" />
                Export PDF
              </GlassAccentButton>
            )}
          />

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className={GLASS_TH}>Expert</th>
                  <th className={GLASS_TH}>Sessions</th>
                  <th className={GLASS_TH}>Avg Participants</th>
                  <th className={GLASS_TH}>Satisfaction</th>
                  <th className={GLASS_TH}>Topics</th>
                  <th className={GLASS_TH}>Status</th>
                </tr>
              </thead>
              <tbody className="text-gray-800 dark:text-white/[0.85]">
                {breakthroughData.expertPerformance.map((expert) => (
                  <tr key={expert.id} className={GLASS_ROW}>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <GlassAvatar name={expert.name} />
                        <span className="font-medium text-gray-900 dark:text-white">{expert.name}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap tabular-nums">{expert.sessions}</td>
                    <td className="py-3 pr-4 whitespace-nowrap tabular-nums">{expert.avgParticipants}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 tabular-nums">
                        <Star className={`h-4 w-4 ${GLASS_TEXT.amber}`} fill="currentColor" />
                        {expert.avgSatisfaction}
                      </div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {expert.topics.map((topic, index) => (
                          <GlassBadge key={index} tone="blue">{topic}</GlassBadge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 whitespace-nowrap">
                      <GlassBadge tone={expert.status === 'Active' ? 'mint' : 'neutral'}>{expert.status}</GlassBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
        </PinnableCard>

        {/* Participation Breakdown */}
        <PinnableCard id="engagement-breakdown" title="Participant Engagement Breakdown" size="wide">
        <GlassCard className="p-5" delay={260}>
          <GlassCardHeader title="Participant Engagement Breakdown" />
          <ShareDonut data={breakthroughData.participationData} darkMode={darkMode} centerValue="100%" centerLabel="of participants" unit="%" />
        </GlassCard>
        </PinnableCard>
      </div>
    </GlassPage>
  );
};

export default BreakthroughsReports;
